import { DollarSign, Hash, Layers } from 'lucide-react'
import { useMemo, useState } from 'react'
import { EmptyState } from '@/components/ds/EmptyState'
import { PageHeader } from '@/components/ds/PageHeader'
import { Section } from '@/components/ds/Section'
import { useCostReport, type CostReport } from '@/api/endpoints/reports'
import { useActiveChannelFromUrl } from '@/hooks/useActiveChannel'
import { cn } from '@/lib/cn'
import { formatNumber, formatUSD } from '@/lib/format'
import styles from './ReportsPage.module.scss'
import { QueryErrorNotice } from '@/components/feedback/QueryErrorNotice'

const RANGE_OPTIONS = [
  { value: 7, label: '7 dias' },
  { value: 30, label: '30 dias' },
  { value: 90, label: '90 dias' },
] as const

type RangeOption = (typeof RANGE_OPTIONS)[number]['value']

/**
 * Reports — first delivery is the Claude API cost chart. The page is
 * structured to host more reports later (each as its own Section), so the
 * route doesn't need to be re-architected when we add latency, ranking
 * calibration, etc.
 */
export function ReportsPage() {
  const { active, isLoading: channelsLoading } = useActiveChannelFromUrl()
  const [days, setDays] = useState<RangeOption>(30)
  const [scope, setScope] = useState<'all' | 'channel'>('all')

  const channelFilter = scope === 'channel' && active ? active.slug : null
  const cost = useCostReport({ days, channel: channelFilter })

  return (
    <div className={styles.page}>
      <PageHeader
        eyebrow="Relatórios"
        title="Reports"
        description="Custo da aplicação ao longo do tempo. O dado vem da telemetria de chamadas de LLM (input + output + cache + web search)."
      />

      <Section
        title="Custo · LLM"
        description={
          channelsLoading
            ? 'Carregando canal...'
            : scope === 'channel' && active
              ? `Filtrado para ${active.name}.`
              : 'Todos os canais.'
        }
        actions={
          <div className={styles.controls}>
            <ScopeToggle value={scope} disabledChannel={!active} onChange={setScope} />
            <RangeToggle value={days} onChange={setDays} />
          </div>
        }
      >
        {cost.isError ? (
          <QueryErrorNotice
            error={cost.error}
            label="os custos"
            onRetry={() => {
              void cost.refetch()
            }}
          />
        ) : cost.isPending ? (
          <ChartSkeleton />
        ) : cost.data.timeseries.length === 0 ? (
          <EmptyState
            title="Sem dados de custo"
            description="Nenhuma chamada de LLM foi registrada no período. Quando uma factory rodar, os custos aparecem aqui."
          />
        ) : (
          <CostReportView report={cost.data} />
        )}
      </Section>
    </div>
  )
}

interface CostReportViewProps {
  report: CostReport
}

function CostReportView({ report }: CostReportViewProps) {
  const { totalUsd, totalCalls, dailyAvg } = useMemo(() => {
    const totalCalls = report.timeseries.reduce((sum, p) => sum + p.calls, 0)
    return {
      totalUsd: report.total_usd,
      totalCalls,
      dailyAvg: report.timeseries.length > 0 ? report.total_usd / report.timeseries.length : 0,
    }
  }, [report])

  return (
    <>
      <div className={styles.kpiRow}>
        <Kpi
          icon={<DollarSign />}
          label="Total no período"
          value={formatUSD(totalUsd)}
          hint={`${String(report.days)} dias`}
        />
        <Kpi
          icon={<Hash />}
          label="Chamadas"
          value={formatNumber(totalCalls)}
          hint="llm_api_calls"
        />
        <Kpi
          icon={<Layers />}
          label="Média / dia"
          value={formatUSD(dailyAvg)}
          hint="dias com atividade"
        />
      </div>

      <DailyCostChart points={report.timeseries} totalDays={report.days} />

      {report.by_feature.length > 0 ? (
        <FeatureBreakdownTable items={report.by_feature} totalUsd={totalUsd} />
      ) : null}
    </>
  )
}

// ---------------------------------------------------------------------------
// Chart
// ---------------------------------------------------------------------------

interface DailyCostChartProps {
  points: CostReport['timeseries']
  totalDays: number
}

/** SVG bar chart of daily Claude cost. Pure inline SVG — no chart-lib
 *  dependency. Bars are scaled to the max value in the window; days with
 *  no calls are filled in as zero so the x axis stays continuous. */
function DailyCostChart({ points, totalDays }: DailyCostChartProps) {
  const filled = useMemo(() => fillMissingDays(points, totalDays), [points, totalDays])
  const max = filled.reduce((m, p) => Math.max(m, p.total_usd), 0)
  if (max === 0) {
    return (
      <EmptyState
        title="Período sem custo"
        description="Há dias no intervalo, mas nenhum gerou custo."
      />
    )
  }

  const width = 100
  const height = 36
  const barGap = 0.4
  const barWidth = Math.max((width - barGap * filled.length) / filled.length, 0.5)

  // Tick marks — first, middle, last day labels.
  const tickIdxs = filled.length <= 1
    ? [0]
    : [0, Math.floor(filled.length / 2), filled.length - 1]

  return (
    <div className={styles.chartCard}>
      <svg
        className={styles.chart}
        viewBox={`0 0 ${String(width)} ${String(height + 6)}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={`Custo diário em USD nos últimos ${String(totalDays)} dias`}
      >
        {filled.map((p, i) => {
          const h = (p.total_usd / max) * height
          const x = i * (barWidth + barGap)
          const y = height - h
          return (
            <g key={p.date}>
              <rect
                x={x}
                y={y}
                width={barWidth}
                height={Math.max(h, 0.15)}
                className={cn(styles.bar, p.total_usd === 0 && styles.barZero)}
              >
                <title>{`${p.date}: ${formatUSD(p.total_usd)} (${String(p.calls)} calls)`}</title>
              </rect>
            </g>
          )
        })}
      </svg>
      <div className={styles.xAxis}>
        {tickIdxs.map((idx) => (
          <span key={idx} className={styles.xTick} style={{ left: `${String((idx / Math.max(filled.length - 1, 1)) * 100)}%` }}>
            {formatShortDate(filled[idx]?.date ?? '')}
          </span>
        ))}
      </div>
    </div>
  )
}

function fillMissingDays(points: CostReport['timeseries'], totalDays: number): CostReport['timeseries'] {
  if (points.length === 0) return []
  const map = new Map(points.map((p) => [p.date, p]))
  const out: CostReport['timeseries'] = []
  // Walk back ``totalDays`` from today (UTC, matching the backend's
  // ``date_trunc('day', called_at)``) and fill gaps with zero rows so the
  // x axis is continuous instead of compressing weeks of inactivity.
  const today = new Date()
  for (let i = totalDays - 1; i >= 0; i--) {
    const d = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() - i))
    const iso = d.toISOString().slice(0, 10)
    const found = map.get(iso)
    if (found) {
      out.push(found)
    } else {
      out.push({
        date: iso,
        total_usd: 0,
        calls: 0,
        input_tokens: 0,
        output_tokens: 0,
        cache_read_tokens: 0,
        cache_write_tokens: 0,
        web_searches: 0,
      })
    }
  }
  return out
}

function formatShortDate(iso: string): string {
  if (!iso) return ''
  // iso is YYYY-MM-DD; show DD/MM
  const parts = iso.split('-')
  if (parts.length !== 3) return iso
  return `${parts[2] ?? ''}/${parts[1] ?? ''}`
}

// ---------------------------------------------------------------------------
// Feature breakdown table
// ---------------------------------------------------------------------------

interface FeatureBreakdownTableProps {
  items: CostReport['by_feature']
  totalUsd: number
}

function FeatureBreakdownTable({ items, totalUsd }: FeatureBreakdownTableProps) {
  return (
    <div className={styles.featureTable}>
      <div className={cn(styles.featureRow, styles.featureHead)}>
        <span>Feature</span>
        <span style={{ textAlign: 'right' }}>Calls</span>
        <span style={{ textAlign: 'right' }}>USD</span>
        <span style={{ textAlign: 'right' }}>%</span>
      </div>
      {items.map((it) => {
        const pct = totalUsd > 0 ? (it.total_usd / totalUsd) * 100 : 0
        return (
          <div key={it.feature} className={styles.featureRow}>
            <span className={styles.featureName}>{it.feature}</span>
            <span className={styles.featureNum}>{formatNumber(it.calls)}</span>
            <span className={styles.featureNum}>{formatUSD(it.total_usd)}</span>
            <span className={styles.featurePct}>{pct.toFixed(1)}%</span>
          </div>
        )
      })}
    </div>
  )
}

// ---------------------------------------------------------------------------
// KPIs + skeleton + toggles
// ---------------------------------------------------------------------------

interface KpiProps {
  icon: React.ReactNode
  label: string
  value: string
  hint: string
}

function Kpi({ icon, label, value, hint }: KpiProps) {
  return (
    <div className={styles.kpi}>
      <span className={styles.kpiLabel}>
        <span style={{ display: 'inline-flex', verticalAlign: 'middle', marginRight: 6, color: 'var(--brand-ember)' }} aria-hidden>
          {icon}
        </span>
        {label}
      </span>
      <span className={styles.kpiValue}>{value}</span>
      <span className={styles.kpiHint}>{hint}</span>
    </div>
  )
}

function ChartSkeleton() {
  return (
    <>
      <div className={styles.kpiRow}>
        {[0, 1, 2].map((i) => (
          <div key={i} className={cn(styles.kpi, styles.kpiSkeleton)} aria-hidden>
            <div className={cn(styles.skeletonShimmer, styles.skeletonLabel)} />
            <div className={cn(styles.skeletonShimmer, styles.skeletonValue)} />
          </div>
        ))}
      </div>
      <div className={cn(styles.chartCard, styles.skeletonShimmer, styles.skeletonChart)} aria-hidden />
    </>
  )
}

interface RangeToggleProps {
  value: RangeOption
  onChange: (next: RangeOption) => void
}

function RangeToggle({ value, onChange }: RangeToggleProps) {
  return (
    <div className={styles.toggle} role="tablist" aria-label="Período">
      {RANGE_OPTIONS.map((opt) => (
        <button
          key={opt.value}
          type="button"
          role="tab"
          aria-selected={value === opt.value}
          className={cn(styles.toggleBtn, value === opt.value && styles.toggleBtnActive)}
          onClick={() => {
            onChange(opt.value)
          }}
        >
          {opt.label}
        </button>
      ))}
    </div>
  )
}

interface ScopeToggleProps {
  value: 'all' | 'channel'
  disabledChannel: boolean
  onChange: (next: 'all' | 'channel') => void
}

function ScopeToggle({ value, disabledChannel, onChange }: ScopeToggleProps) {
  return (
    <div className={styles.toggle} role="tablist" aria-label="Escopo">
      <button
        type="button"
        role="tab"
        aria-selected={value === 'all'}
        className={cn(styles.toggleBtn, value === 'all' && styles.toggleBtnActive)}
        onClick={() => {
          onChange('all')
        }}
      >
        Todos
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={value === 'channel'}
        disabled={disabledChannel}
        className={cn(styles.toggleBtn, value === 'channel' && styles.toggleBtnActive)}
        onClick={() => {
          onChange('channel')
        }}
      >
        Canal atual
      </button>
    </div>
  )
}
